import { createApp } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import { definePreset } from '@primevue/themes'
import Aura from '@primevue/themes/aura'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import DialogService from 'primevue/dialogservice'
import Tooltip from 'primevue/tooltip'

import App from './App.vue'
import router from './router'
import { initTheme } from './composables/useTheme'

// PrimeVue styles
import 'primeicons/primeicons.css'
import './assets/styles/main.css'

// SendMark theme preset - token values per docs/superpowers/specs/2026-06-11-uiux-redesign-design.md §2
const SendMarkPreset = definePreset(Aura, {
  primitive: {
    blue: {
      50: '#eef6ff', 100: '#d9eaff', 200: '#b6d6ff', 300: '#84baff', 400: '#4f97f7',
      500: '#1f74e0', 600: '#0f5fc9', 700: '#0d4ea3', 800: '#103f80', 900: '#123763', 950: '#0c2342'
    },
    teal: {
      50: '#ecfdfa', 100: '#d3faf3', 200: '#a8f2e6', 300: '#5ee0cf', 400: '#2fc9b6',
      500: '#11b3a3', 600: '#069585', 700: '#0a7569', 800: '#0d5d55', 900: '#114e48', 950: '#042f2b'
    },
    slate: {
      50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1', 400: '#94a3b8',
      500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b', 900: '#0f172a', 950: '#020617'
    }
  },
  semantic: {
    primary: {
      50: '{blue.50}', 100: '{blue.100}', 200: '{blue.200}', 300: '{blue.300}', 400: '{blue.400}',
      500: '{blue.500}', 600: '{blue.600}', 700: '{blue.700}', 800: '{blue.800}', 900: '{blue.900}', 950: '{blue.950}'
    },
    transitionDuration: '0.18s',
    focusRing: { width: '2px', style: 'solid', color: '{primary.400}', offset: '2px' },
    formField: { paddingX: '0.875rem', paddingY: '0.625rem', borderRadius: '7px' },
    content: { borderRadius: '10px' },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff', 50: '{slate.50}', 100: '{slate.100}', 200: '{slate.200}', 300: '{slate.300}',
          400: '{slate.400}', 500: '{slate.500}', 600: '{slate.600}', 700: '{slate.700}',
          800: '{slate.800}', 900: '{slate.900}', 950: '{slate.950}'
        },
        primary: {
          color: '#0f5fc9',
          inverseColor: '#ffffff',
          hoverColor: '#0d4ea3',
          activeColor: '#103f80'
        },
        highlight: {
          background: '#eef6ff',
          focusBackground: '#d9eaff',
          color: '#0d4ea3',
          focusColor: '#103f80'
        }
      },
      dark: {
        // Same 0→950 light→dark orientation as Aura's own dark scheme (verified
        // against @primevue/themes/aura source): dark mode comes from CONSUMER
        // tokens referencing the dark end (content.background={surface.900},
        // formField.background={surface.950}) - do NOT reverse this ramp.
        surface: {
          0: '#ffffff', 50: '#e2e8f0', 100: '#cbd5e1', 200: '#aab8ca', 300: '#94a3b8',
          400: '#7c8ba1', 500: '#64748b', 600: '#46556b', 700: '#334155',
          800: '#253348', 900: '#1e293b', 950: '#0f172a'
        },
        primary: {
          color: '#6bb3f0',
          inverseColor: '#002547',
          hoverColor: '#8bc4f5',
          activeColor: '#abd5fa'
        },
        highlight: {
          background: 'rgba(107, 179, 240, 0.16)',
          focusBackground: 'rgba(107, 179, 240, 0.24)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)'
        }
      }
    }
  }
})

const app = createApp(App)

// Pinia state management
app.use(createPinia())

// Vue Router
app.use(router)

// PrimeVue with SendMark custom theme
app.use(PrimeVue, {
  theme: {
    preset: SendMarkPreset,
    options: {
      prefix: 'p',
      darkModeSelector: '.dark-mode',
      cssLayer: false
    }
  },
  ripple: true
})

// PrimeVue services
app.use(ToastService)
app.use(ConfirmationService)
app.use(DialogService)

// PrimeVue directives
app.directive('tooltip', Tooltip)

// Apply saved theme before first paint to prevent flash
initTheme()

app.mount('#app')
